/**
 * Preuve que le corps d'un Canvas est encore le seul paragraphe physique vide
 * qu'un DOCX neuf porte obligatoirement (modèle Hector vierge ou modèle Word du
 * cabinet).
 *
 * L'autorisation monousage du broker (`initialBlankTemplateParagraphId`)
 * identifie ce paragraphe par l'API `blocks`, qui ne lit que du texte. Juste
 * avant de le retirer, on relit donc aussi la forme SDM : une image, un champ
 * ou un contrôle de contenu n'ont aucun texte visible mais seraient détruits
 * avec lui. SDM/1 est exigé explicitement : un modèle futur pourrait changer le
 * sens d'une collection d'inlines vide sans qu'Hector ait requalifié le geste.
 */

import type { CanvasActionContext } from './textActionExecutors';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Vrai si le PREMIER nœud du corps SDM est un paragraphe sans aucun inline.
 * Relu juste avant de retirer le paragraphe du modèle, APRÈS l'écriture : le
 * corps porte alors l'acte, mais une image ou un champ posé entre-temps dans la
 * ligne vide du haut n'a aucun texte et serait détruit avec elle.
 */
export function lePremierParagrapheEstSansInline(document: unknown): boolean {
  if (!isRecord(document) || document.modelVersion !== 'sdm/1' || !Array.isArray(document.body)) return false;
  const [node] = document.body;
  return isRecord(node)
    && node.kind === 'paragraph'
    && isRecord(node.paragraph)
    && Array.isArray(node.paragraph.inlines)
    && node.paragraph.inlines.length === 0;
}

/** Vrai si le corps SDM complet est exactement un paragraphe sans aucun inline. */
export function estLeCorpsDuModeleWordInitialExact(document: unknown): boolean {
  return lePremierParagrapheEstSansInline(document) && (document as { body: unknown[] }).body.length === 1;
}

async function lireLeCorps(ctx: CanvasActionContext, preuve: (document: unknown) => boolean): Promise<boolean> {
  if (typeof ctx.doc.get !== 'function') return false;
  try {
    return preuve(await ctx.doc.get({}));
  } catch {
    return false;
  }
}

/** Relecture fermée : une API absente ou qui lève ne prouve jamais un corps vide. */
export function leCorpsDuModeleWordInitialEstToujoursExact(ctx: CanvasActionContext): Promise<boolean> {
  return lireLeCorps(ctx, estLeCorpsDuModeleWordInitialExact);
}

/** Relecture fermée de {@link lePremierParagrapheEstSansInline}. */
export function lePremierParagrapheEstToujoursSansInline(ctx: CanvasActionContext): Promise<boolean> {
  return lireLeCorps(ctx, lePremierParagrapheEstSansInline);
}
